// DataContext.js - cleaned implementation
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from './AuthContext';
import {
  userApi,
  studentApi,
  unitApi,
  attendanceApi,
  resultApi,
  feeApi,
  calendarApi,
  noticeApi,
  organizationApi,
  statisticsApi,
  whatsappConfigApi,
  sectionApi,
  academicYearApi
} from '../api/services';
import { getParents, createParent, updateParent, deleteParent } from '../api/services/parentApi';
import SUBJECT_API from '../api/services/subjectApi';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();

  // State
  const [users, setUsers] = useState([]);
  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [results, setResults] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [fees, setFees] = useState([]);
  const [feeStructures, setFeeStructures] = useState([]);
  const [notices, setNotices] = useState([]);
  const [units, setUnits] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [subjectCodes, setSubjectCodes] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [whatsappConfigured, setWhatsappConfigured] = useState(false);
  const [currentAcademicYear, setCurrentAcademicYear] = useState(null);
  const [statistics, setStatistics] = useState({
    dashboard: null,
    users: null,
    students: null,
    classes: null,
    attendance: null,
    fees: null,
    results: null,
    calendar: null,
    notices: null,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState({
    users: false,
    students: false,
    parents: false,
    classes: false,
    results: false,
    attendance: false,
    calendar: false,
    fees: false,
    feeStructures: false,
    notices: false,
    units: false,
    subjects: false,
    subjectCodes: false,
    organization: false,
    dashboard: false,
  });

  // Load initial data when authenticated
  useEffect(() => {
    const loadInitialData = async () => {
      if (isAuthenticated) {
        // Load academic year FIRST - this is critical for all other API calls
        await loadCurrentAcademicYear();
        
        // Then load everything else in parallel
        loadClasses();
        loadParents();
        if (user?.userRole === 'admin') {
          loadFeeStructures();
        }
        loadSubjects();
        loadSubjectCodesForClass(); // optional, can be called with a unitId later
        loadOrganization();
        loadCalendar();
        loadDashboardStats();
      }
    };
    
    loadInitialData();
  }, [isAuthenticated]);

  // ---------- Helper Loaders ----------
  const loadStudents = useCallback(async (unitId = null, sectionId = null) => {
    if (!isAuthenticated) return;
    setLoading(prev => ({ ...prev, students: true }));
    try {
      let response;
      if (unitId) {
        // Use currentAcademicYear if available
        response = await studentApi.getStudentsByUnit(unitId, currentAcademicYear, sectionId);
      } else {
        // Fetch all students for dashboard (with high limit)
        response = await studentApi.getAllStudents({ 
          limit: 1000, 
          academicYear: currentAcademicYear,
          status: 'active'
        });
      }
      if (response?.success) {
        setStudents(response.data || []);
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, students: error.message }));
    } finally {
      setLoading(prev => ({ ...prev, students: false }));
    }
  }, [isAuthenticated, currentAcademicYear]);

  const loadParents = useCallback(async (page = 1, limit = 100, search = '') => {
    if (!isAuthenticated) return;
    setLoading(prev => ({ ...prev, parents: true }));
    try {
      const response = await getParents(page, limit, search);
      if (response.success) {
        setParents(response.data || []);
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, parents: error.message }));
    } finally {
      setLoading(prev => ({ ...prev, parents: false }));
    }
  }, [isAuthenticated]);

  const loadAttendance = useCallback(async (filters = {}) => {
    if (!isAuthenticated) return;
    setLoading(prev => ({ ...prev, attendance: true }));
    try {
      // Prioritize academicYear from filters if present, else use currentAcademicYear
      const fetchFilters = { 
        academicYear: filters.academicYear || currentAcademicYear,
        ...filters 
      };
      
      const response = await statisticsApi.getAttendanceStats(fetchFilters);
      if (response.success) {
        setStatistics(prev => ({ ...prev, attendance: response.data }));
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, attendance: error.message }));
    } finally {
      setLoading(prev => ({ ...prev, attendance: false }));
    }
  }, [isAuthenticated, currentAcademicYear]);

  const loadCalendar = useCallback(async (filters = {}) => {
    if (!isAuthenticated) return;
    setLoading(prev => ({ ...prev, calendar: true }));
    try {
      // Default to 1 year back and 1 year forward if no filters provided
      const today = new Date();
      const lastYear = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
      const nextYear = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());

      const fromDate = filters.from || lastYear.toISOString().split('T')[0];
      const toDate = filters.to || nextYear.toISOString().split('T')[0];
      
      const response = await calendarApi.getEventsByRange(fromDate, toDate);
      
      if (response.success) {
        // Handle potential double-nested response structure
        let eventsData = response.data;
        if (!Array.isArray(eventsData) && eventsData?.data && Array.isArray(eventsData.data)) {
           eventsData = eventsData.data;
        }
        
        setCalendar(Array.isArray(eventsData) ? eventsData : []);
      } else {
        setErrors(prev => ({ ...prev, calendar: response.message }));
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, calendar: error.message }));
    } finally {
      setLoading(prev => ({ ...prev, calendar: false }));
    }
  }, [isAuthenticated]);

  const loadDashboardStats = useCallback(async (filters = {}) => {
    if (!isAuthenticated) return;
    setLoading(prev => ({ ...prev, dashboard: true }));
    try {
      const fetchFilters = { 
        academicYear: filters.academicYear || currentAcademicYear,
        ...filters 
      };
      
      const response = await statisticsApi.getDashboardStats(fetchFilters);
      if (response.success) {
        setStatistics(prev => ({ ...prev, dashboard: response.data }));
      }
    } catch (error) {
    } finally {
      setLoading(prev => ({ ...prev, dashboard: false }));
    }
  }, [isAuthenticated, currentAcademicYear]);

  const loadFees = useCallback(async (filters = {}) => {
    if (!isAuthenticated) return;
    setLoading(prev => ({ ...prev, fees: true }));
    try {
      const response = await feeApi.getPendingFees(filters.unitId, filters.academicYear || currentAcademicYear);
      if (response.success) {
        setFees(response.data || []);
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, fees: error.message }));
    } finally {
      setLoading(prev => ({ ...prev, fees: false }));
    }
    // Load dashboard statistics as well
    try {
      const statsResponse = await statisticsApi.getDashboardStats({ academicYear: currentAcademicYear });
      if (statsResponse.success) {
        setStatistics(prev => ({ ...prev, dashboard: statsResponse.data }));
      }
    } catch (statsError) {
    }
  }, [isAuthenticated, currentAcademicYear]);

  const loadFeeStructures = useCallback(async () => {
    if (!isAuthenticated || user?.userRole !== 'admin') return;
    setLoading(prev => ({ ...prev, feeStructures: true }));
    try {
      const response = await feeApi.getFeeStructures();
      if (response.success) {
        setFeeStructures(response.data || []);
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, feeStructures: error.message }));
    } finally {
      setLoading(prev => ({ ...prev, feeStructures: false }));
    }
  }, [isAuthenticated]);

  const loadSubjects = useCallback(async (filters = {}) => {
    if (!isAuthenticated) return;
    setLoading(prev => ({ ...prev, subjects: true }));
    try {
      // Prioritize academicYear from filters if present, else use currentAcademicYear
      const fetchFilters = { 
        academicYear: filters.academicYear || currentAcademicYear,
        ...filters 
      };
      
      const response = await SUBJECT_API.getAllSubjects(fetchFilters);
      if (response.success) {
        setSubjects(response.data || []);
      }
    } catch (error) {
      const message = error.message || 'Failed to load subjects';
      setErrors(prev => ({ ...prev, subjects: message }));
    } finally {
      setLoading(prev => ({ ...prev, subjects: false }));
    }
  }, [isAuthenticated, currentAcademicYear]);

  const loadSubjectCodesForClass = useCallback(async (unitId) => {
    if (!isAuthenticated) return;
    if (!unitId) {
      return;
    }
    setLoading(prev => ({ ...prev, subjectCodes: true }));
    try {
      const response = await SUBJECT_API.getSubjectCodesForClass(unitId, currentAcademicYear);
      if (response.success) {
        setSubjectCodes(response.data || []);
      }
    } catch (error) {
      const message = error.message || 'Failed to load subject codes';
      setErrors(prev => ({ ...prev, subjectCodes: message }));
    } finally {
      setLoading(prev => ({ ...prev, subjectCodes: false }));
    }
  }, [isAuthenticated, currentAcademicYear]);

  const loadClasses = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(prev => ({ ...prev, classes: true }));
    try {
      const response = await unitApi.getAllUnits();
      if (response.success) {
        const units = response.data || [];
        
        // Fetch sections for each unit
        const unitsWithSections = await Promise.all(units.map(async (unit) => {
             try {
                 const secRes = await sectionApi.getSectionsByUnit(unit._id);
                 return {
                     ...unit,
                     sections: secRes.success ? secRes.data : []
                 };
             } catch (e) {
                 return { ...unit, sections: [] }; // Fallback to empty sections
             }
        }));

        setClasses(unitsWithSections);
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, classes: error.message }));
    } finally {
      setLoading(prev => ({ ...prev, classes: false }));
    }
  }, [isAuthenticated]);

  const loadUsers = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(prev => ({ ...prev, users: true }));
    try {
      const response = await userApi.getAllUsers();
      if (response.success) {
        setUsers(response.data || []);
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, users: error.message }));
    } finally {
      setLoading(prev => ({ ...prev, users: false }));
    }
  }, [isAuthenticated]);

  const loadOrganization = useCallback(async () => {
      if (!isAuthenticated) return;
      setLoading(prev => ({ ...prev, organization: true }));
      try {
          const response = await organizationApi.getOrganizationDetails();
          if (response.success && response.data) {
              setOrganization(response.data);
              // DO NOT set academic year from organization - it comes from settings API
              
              // Apply Branding if available
              // Check for brandColors at root level OR inside branding object
              const brandColors = response.data.brandColors || response.data.branding?.brandColors;
              
              if (brandColors) {
                const { primary, secondary } = brandColors;
                if (primary) {
                  document.documentElement.style.setProperty('--primary-color', primary);
                }
                if (secondary) {
                  document.documentElement.style.setProperty('--secondary-color', secondary);
                }
              }
          }
      } catch (error) {
          setErrors(prev => ({ ...prev, organization: error.message }));
      } finally {
          setLoading(prev => ({ ...prev, organization: false }));
      }
  }, [isAuthenticated]);

  const loadCurrentAcademicYear = useCallback(async (forceRefresh = false) => {
    if (!isAuthenticated) return;
    
    try {
      // Check localStorage first (unless force refresh is requested)
      if (!forceRefresh) {
        const cachedYear = localStorage.getItem('currentAcademicYear');
        if (cachedYear) {
          setCurrentAcademicYear(cachedYear);
          return;
        }
      }
      
      // Fetch from API if not in localStorage or force refresh
      const response = await academicYearApi.getCurrent();
      if (response.success && response.data) {
        // Assume response.data is the academic year object or string
        const year = response.data.academicYear || response.data.year || (typeof response.data === 'string' ? response.data : null);
        if (year) {
          setCurrentAcademicYear(year);
          // Store in localStorage for future use
          localStorage.setItem('currentAcademicYear', year);
        } else {
        }
      } else {
      }
    } catch (error) {
    }
  }, [isAuthenticated]);

  // Utility function to update current academic year (e.g., when user selects a different year)
  const updateCurrentAcademicYear = useCallback((year) => {
    if (year) {
      setCurrentAcademicYear(year);
      localStorage.setItem('currentAcademicYear', year);
    }
  }, []);

  const loadWhatsappConfig = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await whatsappConfigApi.getConfiguration();
      if (response.success && response.data && response.data.phoneNumberId) {
        setWhatsappConfigured(true);
      } else {
        setWhatsappConfigured(false);
      }
    } catch (error) {
       setWhatsappConfigured(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      loadWhatsappConfig();
    }
  }, [isAuthenticated, loadWhatsappConfig]);

  // ---------- Utility Functions ----------
  const getClassesForTeacher = useCallback(teacherId => {
    return classes;
  }, [classes]);

  const getStudentsForTeacher = useCallback(teacherId => {
    return students;
  }, [students]);

  const getParentsForTeacher = useCallback(teacherId => {
    return parents;
  }, [parents]);

  const getAttendanceForTeacher = useCallback(teacherId => {
    return attendance;
  }, [attendance]);

  const markAttendance = useCallback(async (attendanceData) => {
    try {
      // Map the data from the component structure to the API structure
      // Attendance.js sends: { unitId, date, attendanceData, absentStudents }
      // attendanceApi.bulkMarkAttendance expects: { date, isHoliday, records: [{ studentEnrollmentId, status }] }
      const bulkData = {
        date: attendanceData.date,
        isHoliday: false,
        unitId: attendanceData.unitId,
        sectionId: attendanceData.sectionId, // Pass sectionId if provided
        records: Object.entries(attendanceData.attendanceData).map(([studentId, status]) => ({
          studentEnrollmentId: studentId,
          status: status
        }))
      };
      
      const response = await attendanceApi.bulkMarkAttendance(bulkData);
      if (response.success) {
        await loadDashboardStats();
        return { success: true };
      }
      return { success: false, message: response.message };
    } catch (e) {
      return { success: false, message: e.message };
    }
  }, [loadDashboardStats]);

  // ---------- CRUD Operations ----------
  const addUser = async userData => {
    try {
      const response = await userApi.createUser(userData);
      if (response.success) {
        await loadUsers();
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (e) {
      return { success: false, message: e.message };
    }
  };

  const updateUser = async (userId, userData) => {
    try {
      const response = await userApi.updateUser(userId, userData);
      if (response.success) {
        await loadUsers();
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (e) {
      return { success: false, message: e.message };
    }
  };

  const deleteUser = async userId => {
    try {
      const response = await userApi.deleteUser(userId);
      if (response.success) {
        await loadUsers();
        return { success: true };
      }
      return { success: false, message: response.message };
    } catch (e) {
      return { success: false, message: e.message };
    }
  };

  const addStudent = async studentData => {
    try {
      const response = await studentApi.registerStudent(studentData);
      if (response.success) {
        await loadStudents();
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (e) {
      return { success: false, message: e.message };
    }
  };

  const updateStudent = async (registrationId, studentData) => {
    try {
      const response = await studentApi.updateStudent(registrationId, studentData);
      if (response.success) {
        await loadStudents();
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (e) {
      return { success: false, message: e.message };
    }
  };

  const updateStudentById = async (id, studentData) => {
    try {
      const response = await studentApi.updateStudentById(id, studentData);
      if (response.success) {
        await loadStudents();
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (e) {
      return { success: false, message: e.message };
    }
  };

  const deleteStudent = async registrationId => {
    try {
      const response = await studentApi.deleteStudent(registrationId);
      if (response.success) {
        await loadStudents();
        return { success: true };
      }
      return { success: false, message: response.message };
    } catch (e) {
      return { success: false, message: e.message };
    }
  };

  const addParent = async parentData => {
    try {
      const response = await createParent(parentData);
      if (response.success) {
        await loadParents();
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (e) {
      return { success: false, message: e.message };
    }
  };

  const updateParentData = async (parentId, parentData) => {
    try {
      const response = await updateParent(parentId, parentData);
      if (response.success) {
        await loadParents();
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (e) {
      return { success: false, message: e.message };
    }
  };

  const deleteParentData = async parentId => {
    try {
      const response = await deleteParent(parentId);
      if (response.success) {
        await loadParents();
        return { success: true };
      }
      return { success: false, message: response.message };
    } catch (e) {
      return { success: false, message: e.message };
    }
  };

  const addClass = async classData => {
    try {
      const response = await unitApi.createUnit(classData);
      if (response.success) {
        await loadClasses();
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (e) {
      return { success: false, message: e.message };
    }
  };

  const updateClass = async (unitId, classData) => {
    try {
      const response = await unitApi.updateUnit(unitId, classData);
      if (response.success) {
        await loadClasses();
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (e) {
      return { success: false, message: e.message };
    }
  };

  const deleteClass = async unitId => {
    try {
      const response = await unitApi.deleteUnit(unitId);
      if (response.success) {
        await loadClasses();
        return { success: true };
      }
      return { success: false, message: response.message };
    } catch (e) {
      return { success: false, message: e.message };
    }
  };

  const addCalendarEvent = async (eventData) => {
    try {
      const response = await calendarApi.createEvent(eventData);
      if (response.success) {
        await loadCalendar();
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (e) {
      return { success: false, message: e.message };
    }
  };

  const updateCalendarEvent = async (eventId, eventData) => {
    try {
      const response = await calendarApi.updateEvent(eventId, eventData);
      if (response.success) {
        await loadCalendar();
        return { success: true, data: response.data };
      }
      return { success: false, message: response.message };
    } catch (e) {
      return { success: false, message: e.message };
    }
  };

  const deleteCalendarEvent = async (eventId) => {
    try {
      const response = await calendarApi.deleteEvent(eventId);
      if (response.success) {
        await loadCalendar();
        return { success: true };
      }
      return { success: false, message: response.message };
    } catch (e) {
      return { success: false, message: e.message };
    }
  };

  // ---------- Exported Context Value ----------
  const value = useMemo(() => ({
    users,
    students,
    parents,
    classes,
    results,
    attendance,
    calendar,
    fees,
    feeStructures,
    notices,
    units,
    subjects,
    subjectCodes,
    organization,
    whatsappConfigured,
    currentAcademicYear,
    statistics,
    errors,
    loading,
    // Loaders
    loadDashboardStats,
    loadStudents,
    loadParents,
    loadCalendar,
    loadFees,
    loadAttendance,
    loadFeeStructures,
    loadSubjects,
    loadSubjectCodesForClass,
    loadClasses,
    loadUsers,
    loadOrganization,
    loadCurrentAcademicYear,
    loadWhatsappConfig, // Expose the new loader
    updateCurrentAcademicYear, // Utility to update academic year
    // CRUD Functionslity
    getClassesForTeacher,
    getStudentsForTeacher,
    getParentsForTeacher,
    getAttendanceForTeacher,
    markAttendance,
    // CRUD
    addUser,
    updateUser,
    deleteUser,
    addStudent,
    updateStudent,
    updateStudentById,
    deleteStudent,
    addParent,
    updateParentData,
    deleteParentData,
    addClass,
    updateClass,
    deleteClass,
    addCalendarEvent,
    updateCalendarEvent,
    deleteCalendarEvent,
  }), [
    users, students, parents, classes, results, attendance, calendar, fees,
    feeStructures, notices, units, subjects, subjectCodes, organization,
    currentAcademicYear, statistics, errors, loading,
    loadDashboardStats, loadStudents, loadParents, loadCalendar, loadFees, loadAttendance,
    loadFeeStructures, loadSubjects, loadSubjectCodesForClass, loadClasses,
    loadUsers, loadOrganization, loadCurrentAcademicYear, updateCurrentAcademicYear,
    getClassesForTeacher, getStudentsForTeacher, getParentsForTeacher, getAttendanceForTeacher,
    markAttendance,
    addUser, updateUser, deleteUser, addStudent, updateStudent, updateStudentById,
    deleteStudent, addParent, updateParentData, deleteParentData, addClass,
    updateClass, deleteClass, addCalendarEvent, updateCalendarEvent, deleteCalendarEvent
  ]);

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

DataProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
